/**
 * The "here is what changed" modal, shown once per release and never again.
 *
 * This file is the decision half — the release list and the seen/not-seen rules — deliberately
 * free of React and of `window`, so the gate is a table test rather than a browser simulation.
 * Same shape as `src/installPrompt.ts`; the wiring lives in `src/app/useUpdateNotice.ts` and the
 * panel in `src/components/UpdateNoticeModal.tsx`.
 *
 * To announce the next release: add one entry to the end of `UPDATE_NOTICES` with a new `id`.
 * Nothing else. Only the newest unseen entry is ever shown, so someone returning after three
 * releases gets one modal rather than a queue of three.
 */

/** Illustration keys the modal knows how to render. A string rather than a component so this file
 * stays JSX-free and testable on its own. */
export type UpdateNoticeVisual = 'scoring-cards'

export interface UpdateNotice {
  /** Stable forever, never reused: the dismissal record stores this string. */
  id: string
  /** The day it shipped, `YYYY-MM-DD`. Shown beside the "Update" header. */
  date: string
  title: string
  /** Short paragraphs. Two or three — this is a notice, not a changelog. */
  body: string[]
  visual?: UpdateNoticeVisual
  /** Primary button label. */
  cta?: string
}

/** Oldest first. Order is what "newest" means below, so never re-sort it. */
export const UPDATE_NOTICES: UpdateNotice[] = [
  {
    // Re-announced: the first version of this notice shipped with frozen cards, so everyone who
    // dismissed it would never see the motion. A new id is how a release is shown again — the old
    // one stays in people's seen list and is simply never offered.
    id: '2026-09-tier-colours-r2',
    date: '2026-09-07',
    title: 'New colours and animations',
    body: [
      'An exact score is gold now, with a shine to match. A partial hit is steel.',
      'Same points, same rules, same totals, same history. Only the look has changed.',
    ],
    visual: 'scoring-cards',
    cta: 'Got it',
  },
]

/** Same wording and format as How to Play's "last updated" line, so the two dates read alike. */
export function formatNoticeDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/** Same `rwbet:` convention as `THEME_STORAGE_KEY` (src/storage.ts). */
export const UPDATE_NOTICE_STORAGE_KEY = 'rwbet:update-notice'

export interface UpdateNoticeState {
  seenIds: string[]
}

export const INITIAL_UPDATE_NOTICE_STATE: UpdateNoticeState = { seenIds: [] }

/** Delay between the app painting and the notice arriving, so it lands on a settled screen. */
export const UPDATE_NOTICE_DELAY_MS = 1500

/** Structural, matching `src/storage.ts` — this file must type-check without the DOM lib. */
interface MinimalStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

function getLocalStorage(): MinimalStorage | undefined {
  return (globalThis as { localStorage?: MinimalStorage }).localStorage
}

/**
 * `null` means storage is unreachable (private browsing throws), and callers treat that as *show
 * nothing*. A notice we cannot mark as seen would greet the user on every single load, which is
 * worse than never showing it at all.
 *
 * A missing or corrupt record is a different thing, and reads as "seen nothing yet".
 */
export function loadUpdateNoticeState(): UpdateNoticeState | null {
  const storage = getLocalStorage()
  if (!storage) return null
  let raw: string | null
  try {
    raw = storage.getItem(UPDATE_NOTICE_STORAGE_KEY)
  } catch {
    // Reading itself threw — storage is unreachable, so fail closed.
    return null
  }
  if (!raw) return INITIAL_UPDATE_NOTICE_STATE
  try {
    const parsed = JSON.parse(raw) as Partial<UpdateNoticeState>
    return {
      seenIds: Array.isArray(parsed.seenIds)
        ? parsed.seenIds.filter((id): id is string => typeof id === 'string')
        : [],
    }
  } catch {
    // A corrupt record is not the same as no storage: read it as "seen nothing" and let the next
    // dismissal overwrite it. Failing closed here would suppress every future notice forever.
    return INITIAL_UPDATE_NOTICE_STATE
  }
}

export function saveUpdateNoticeState(state: UpdateNoticeState): void {
  try {
    getLocalStorage()?.setItem(UPDATE_NOTICE_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Nothing to do — the next read fails closed for the same reason.
  }
}

export interface PendingNoticeInput {
  notices: UpdateNotice[]
  /** `null` when storage is unavailable; see `loadUpdateNoticeState`. */
  state: UpdateNoticeState | null
}

/** The newest entry the user has not seen, or `null` if they are up to date. */
export function pendingNotice({ notices, state }: PendingNoticeInput): UpdateNotice | null {
  if (!state) return null
  for (let i = notices.length - 1; i >= 0; i--) {
    const notice = notices[i]
    if (notice && !state.seenIds.includes(notice.id)) return notice
  }
  return null
}

/**
 * Marks the shown notice seen *and every entry before it*: showing the newest one is the answer to
 * all of them, and leaving older ids unmarked would hand the user last month's news tomorrow.
 */
export function markSeen(
  state: UpdateNoticeState,
  notices: UpdateNotice[],
  shownId: string
): UpdateNoticeState {
  const index = notices.findIndex((notice) => notice.id === shownId)
  // An id that is not in the list any more still counts as seen — dropping it would loop.
  const upTo = index === -1 ? [] : notices.slice(0, index + 1).map((notice) => notice.id)
  const seenIds = [...state.seenIds]
  for (const id of [...upTo, shownId]) {
    if (!seenIds.includes(id)) seenIds.push(id)
  }
  return { seenIds }
}
